import React, { useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createRoot } from 'react-dom/client';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { useColorScheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useNavigate } from 'react-router-dom';
import { formatTimeAgo } from '../internals/utils';

const hoverTextStyles = {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '5px',
  borderRadius: '5px',
  pointerEvents: 'none',
  whiteSpace: 'pre-line', // Changed from 'nowrap' to 'pre-line' to preserve line breaks
};

const calculateHoverPosition = (mapRect, hoverRect, cursorX, cursorY) => {
  // Calculate available space
  const spaceRight = mapRect.width - cursorX;
  const spaceLeft = cursorX;
  const spaceBottom = mapRect.height - cursorY;
  
  let left, top;
  
  // Horizontal positioning with centering
  const halfWidth = hoverRect.width / 2;
  if (spaceRight >= halfWidth && spaceLeft >= halfWidth) {
    // Enough space on both sides, center it
    left = cursorX - halfWidth;
  } else if (spaceRight >= hoverRect.width + 10) {
    // Only enough space on right, position to the right
    left = cursorX + 10;
  } else {
    // Position to the left
    left = cursorX - hoverRect.width - 10;
  }
  
  // Vertical positioning
  if (spaceBottom >= hoverRect.height + 10) {
    top = cursorY + 10;
  } else {
    top = cursorY - hoverRect.height - 10;
  }
  
  // Ensure the popup stays within map bounds
  return {
    left: Math.max(10, Math.min(mapRect.width - hoverRect.width - 10, left)),
    top: Math.max(10, Math.min(mapRect.height - hoverRect.height - 10, top))
  };
};

const createHoverText = (content, mapDiv) => {
  const hoverTextDiv = document.createElement('div');
  Object.assign(hoverTextDiv.style, hoverTextStyles);
  hoverTextDiv.innerHTML = content;
  hoverTextDiv.classList.add('hover-text');
  mapDiv.appendChild(hoverTextDiv);
  return hoverTextDiv;
};

const updateHoverPosition = (hoverTextDiv, mapDiv, moveEvent, calculatePosition = true) => {
  if (!hoverTextDiv || !mapDiv) return;
  
  const mapRect = mapDiv.getBoundingClientRect();
  const hoverRect = hoverTextDiv.getBoundingClientRect();
  
  if (calculatePosition) {
    const { left, top } = calculateHoverPosition(
      mapRect,
      hoverRect,
      moveEvent.clientX - mapRect.left,
      moveEvent.clientY - mapRect.top
    );
    
    hoverTextDiv.style.left = `${left}px`;
    hoverTextDiv.style.top = `${top}px`;
  }
};

const removeHoverText = () => {
  document.querySelectorAll('.hover-text').forEach(el => el.remove());
};

const getRandomOffset = (maxMeters) => {
  const maxDegrees = maxMeters / 111320; // 1 degree of latitude/longitude is approximately 111.32 km
  return (Math.random() - 0.5) * 2 * maxDegrees;
};

const generateRandomPositions = (basePosition, count, maxDistance) => {
  return Array.from({ length: count }, () => ({
    lat: basePosition.lat + getRandomOffset(maxDistance),
    lng: basePosition.lng + getRandomOffset(maxDistance),
  }));
};

const basePositions = {
  gurgaon: { lat: 28.370108, lng: 76.922603 },
  noida: { lat: 28.535517, lng: 77.391029 },
  delhi: { lat: 28.704060, lng: 77.102493 },
};

const demoPositions = [
  ...generateRandomPositions(basePositions.gurgaon, 20, 10000), // 20 positions within 10 km of Gurgaon
  ...generateRandomPositions(basePositions.noida, 10, 10000), // 10 positions within 10 km of Noida
  ...generateRandomPositions(basePositions.delhi, 10, 10000), // 10 positions within 10 km of Delhi
];

const markerIcons = {
  red: '/assets/img/IGT Red.png',
  blue: '/assets/img/IGT Blu.png',
  black: '/assets/img/IGT Black.png',
};

const MarkerContent = memo(({ iconSrc, label, width, height }) => {
  const style = {
    position: 'relative',
    width: width ? `${width}px` : 'auto',
    height: height ? `${height}px` : 'auto',
  };

  return (
    <div style={style}>
      <img src={iconSrc} style={{ maxWidth: '100%', maxHeight: '100%', height: 'auto', width: 'auto' }} alt="Marker" />
      {label && (
        <div
          style={{
            position: 'absolute',
            bottom: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Transparent black background
            borderRadius: '50%', // Circle shape
            width: '30px', // Fixed width for perfect circle
            height: '30px', // Fixed height for perfect circle
            display: 'flex', // Center the text inside the circle
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
});

const renderReactToDOM = (Component, props) => {
  const div = document.createElement('div');
  const root = createRoot(div);
  root.render(<Component {...props} />);
  return div;
};

const defaultCenter = { lat: 28.540887385150693, lng: 77.14643226028036 };

const calculateMapCenter = (positions) => {
  if (!positions || positions.length === 0) {
    // console.log('No positions, using default center:', defaultCenter);
    return defaultCenter;
  }

  const validPositions = positions.filter(pos => pos.latitude != null && pos.longitude != null);
  if (validPositions.length === 0) {
    // console.log('No valid positions, using default center:', defaultCenter);
    return defaultCenter;
  }

  // Sort coordinates
  const lats = validPositions.map(pos => Number(pos.latitude)).sort((a, b) => a - b);
  const lngs = validPositions.map(pos => Number(pos.longitude)).sort((a, b) => a - b);

  // Calculate quartiles for latitude
  const latQ1Index = Math.floor(lats.length * 0.25);
  const latQ3Index = Math.floor(lats.length * 0.75);
  const latQ1 = lats[latQ1Index];
  const latQ3 = lats[latQ3Index];
  const latIQR = latQ3 - latQ1;
  const latLowerBound = latQ1 - 1.5 * latIQR;
  const latUpperBound = latQ3 + 1.5 * latIQR;

  // Calculate quartiles for longitude
  const lngQ1Index = Math.floor(lngs.length * 0.25);
  const lngQ3Index = Math.floor(lngs.length * 0.75);
  const lngQ1 = lngs[lngQ1Index];
  const lngQ3 = lngs[lngQ3Index];
  const lngIQR = lngQ3 - lngQ1;
  const lngLowerBound = lngQ1 - 1.5 * lngIQR;
  const lngUpperBound = lngQ3 + 1.5 * lngIQR;

  // Filter out outliers
  const filteredPositions = validPositions.filter(pos => {
    const lat = Number(pos.latitude);
    const lng = Number(pos.longitude);
    return lat >= latLowerBound && lat <= latUpperBound && 
           lng >= lngLowerBound && lng <= lngUpperBound;
  });

  if (filteredPositions.length === 0) {
    // console.log('All points were considered outliers, using median of all points');
    const medianLat = lats[Math.floor(lats.length / 2)];
    const medianLng = lngs[Math.floor(lngs.length / 2)];
    return { lat: medianLat, lng: medianLng };
  }

  // Calculate center from non-outlier positions
  const center = {
    lat: filteredPositions.reduce((sum, pos) => sum + Number(pos.latitude), 0) / filteredPositions.length,
    lng: filteredPositions.reduce((sum, pos) => sum + Number(pos.longitude), 0) / filteredPositions.length
  };

  // console.log('Calculated center using IQR method:', {
  //   center,
  //   totalPoints: validPositions.length,
  //   pointsAfterOutlierRemoval: filteredPositions.length,
  //   outliers: validPositions.length - filteredPositions.length
  // });
  return center;
};

function FleetMapContent({ positions, loading }) {
  const googleMap = useMap();
  const markerClustererRef = useRef(null);
  const navigate = useNavigate();
  const activeHoverRef = useRef(null);
  const MIN_ZOOM_LEVEL = 11;

  const handleClusterClick = (cluster) => {
    console.log('Cluster clicked:', cluster);
  };

  const createMarkerHoverContent = (position) => (
    `🔋 ${position.battery_pack_id}<br> 🛵 ${position.VID}<br>🕒 ${formatTimeAgo(position.last_updated,false)}`
  );

  const createClusterHoverContent = (markers) => (
    markers.map(marker => 
      `🔋 ${marker.customData.packID} 🛵 ${marker.customData.VID} 🕒 ${formatTimeAgo(marker.customData.last_updated,false)}`
    ).join('<br>')
  );

  const renderer = useMemo(() => ({
    render: ({ markers, _position: position }) => {
      const markerDiv = renderReactToDOM(MarkerContent, {
        iconSrc: "/assets/img/IGT Black.png",
        label: String(markers.length),
        width: 20,
      });

      const clusterElement = new window.google.maps.marker.AdvancedMarkerElement({
        position: {
          lat: parseFloat(position.lat()),
          lng: parseFloat(position.lng())
        },
        content: markerDiv,
      });

      clusterElement.addEventListener('mouseover', (event) => {
        const content = createClusterHoverContent(markers);
        const hoverTextDiv = createHoverText(content, googleMap.getDiv());
        activeHoverRef.current = hoverTextDiv;

        const moveHandler = (moveEvent) => {
          hoverTextDiv.lastEvent = moveEvent;
          updateHoverPosition(hoverTextDiv, googleMap.getDiv(), moveEvent);
        };

        clusterElement.addEventListener('mousemove', moveHandler);
        moveHandler(event);
      });

      clusterElement.addEventListener('mouseout', () => {
        removeHoverText();
        activeHoverRef.current = null;
      });

      return clusterElement;
    },
  }), [googleMap]);

  // Pan to new center when positions change
  useEffect(() => {
    if (googleMap && positions.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Sort coordinates for IQR calculation
      const lats = positions.map(pos => Number(pos.latitude)).sort((a, b) => a - b);
      const lngs = positions.map(pos => Number(pos.longitude)).sort((a, b) => a - b);

      // Calculate quartiles for latitude
      const latQ1Index = Math.floor(lats.length * 0.25);
      const latQ3Index = Math.floor(lats.length * 0.75);
      const latQ1 = lats[latQ1Index];
      const latQ3 = lats[latQ3Index];
      const latIQR = latQ3 - latQ1;
      const latLowerBound = latQ1 - 1.5 * latIQR;
      const latUpperBound = latQ3 + 1.5 * latIQR;

      // Calculate quartiles for longitude
      const lngQ1Index = Math.floor(lngs.length * 0.25);
      const lngQ3Index = Math.floor(lngs.length * 0.75);
      const lngQ1 = lngs[lngQ1Index];
      const lngQ3 = lngs[lngQ3Index];
      const lngIQR = lngQ3 - lngQ1;
      const lngLowerBound = lngQ1 - 1.5 * lngIQR;
      const lngUpperBound = lngQ3 + 1.5 * lngIQR;

      let hasValidPositions = false;
      positions.forEach(pos => {
        if (pos.latitude && pos.longitude) {
          const lat = Number(pos.latitude);
          const lng = Number(pos.longitude);
          if (lat >= latLowerBound && lat <= latUpperBound && 
              lng >= lngLowerBound && lng <= lngUpperBound) {
            bounds.extend({ lat, lng });
            hasValidPositions = true;
          }
        }
      });

      if (hasValidPositions) {
        googleMap.fitBounds(bounds);
        
        // Add a listener to enforce minimum zoom after bounds are set
        const boundsChangedListener = googleMap.addListener('bounds_changed', () => {
          const currentZoom = googleMap.getZoom();
          if (currentZoom && currentZoom < MIN_ZOOM_LEVEL) {
            googleMap.setZoom(MIN_ZOOM_LEVEL);
          }
          // Remove the listener after it fires once
          window.google.maps.event.removeListener(boundsChangedListener);
        });
      }
    }
  }, [googleMap, positions]);

  // Handle zoom events
  useEffect(() => {
    if (googleMap) {
      const zoomListener = googleMap.addListener('zoom_changed', () => {
        if (activeHoverRef.current) {
          removeHoverText();
          activeHoverRef.current = null;
        }
      });

      const zoomEndListener = googleMap.addListener('idle', () => {
        if (activeHoverRef.current && activeHoverRef.current.lastEvent) {
          const element = document.elementFromPoint(
            activeHoverRef.current.lastEvent.clientX,
            activeHoverRef.current.lastEvent.clientY
          );
          if (element && element.closest('.hover-text')) {
            updateHoverPosition(activeHoverRef.current, googleMap.getDiv(), activeHoverRef.current.lastEvent);
          } else {
            removeHoverText();
            activeHoverRef.current = null;
          }
        }
      });

      return () => {
        window.google.maps.event.removeListener(zoomListener);
        window.google.maps.event.removeListener(zoomEndListener);
      };
    }
  }, [googleMap]);

  // Initialize marker clusterer
  useEffect(() => {
    if (googleMap && !markerClustererRef.current) {
      markerClustererRef.current = new MarkerClusterer({
        map: googleMap,
        markers: [],
        onClusterClick: handleClusterClick,
        renderer: renderer,
      });
    }
  }, [googleMap, handleClusterClick, renderer]);

  // Update markers when positions change
  useEffect(() => {
    if (markerClustererRef.current && window.google && window.google.maps) {
      markerClustererRef.current.clearMarkers();
      const markers = positions.map((position, index) => {
        const markerDiv = renderReactToDOM(MarkerContent, {
          iconSrc: markerIcons[position.model_casing],
          width: 20,
        });
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: new window.google.maps.LatLng(parseFloat(position.latitude), parseFloat(position.longitude)),
          content: markerDiv,
        });

        marker.addEventListener('click', () => {
          navigate(`/battery-packs/${position.battery_pack_IMEI}`);
        });

        marker.customData = { 
          packID: position.battery_pack_id, 
          VID: position.VID, 
          last_updated: position.last_updated 
        };

        marker.addEventListener('mouseover', (event) => {
          const content = createMarkerHoverContent(position);
          const hoverTextDiv = createHoverText(content, googleMap.getDiv());
          activeHoverRef.current = hoverTextDiv;

          const moveHandler = (moveEvent) => {
            hoverTextDiv.lastEvent = moveEvent;
            updateHoverPosition(hoverTextDiv, googleMap.getDiv(), moveEvent);
          };

          marker.addEventListener('mousemove', moveHandler);
          moveHandler(event);
        });

        marker.addEventListener('mouseout', () => {
          removeHoverText();
          activeHoverRef.current = null;
        });

        return marker;
      });
      markerClustererRef.current.addMarkers(markers);
    }
  }, [positions, googleMap, navigate]);

  return (
    <>
      {loading && <CircularProgress />}
    </>
  );
}

export default function FleetMap({ positions = demoPositions, imageSrc, pinColor, loading }) {
  const { mode } = useColorScheme();
  const mapContainerStyle = { 
    width: '100vw', 
    height: '400px', 
    borderRadius: '50px' 
  };
  const colorSchemeMap = { 
    light: "LIGHT", 
    dark: "DARK", 
    system: "FOLLOW_SYSTEM" 
  };
  
  const mapRef = useRef(null);
  const initialCenter = useMemo(() => calculateMapCenter(positions), [positions]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (positions.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      positions.forEach(pos => {
        if (pos.latitude && pos.longitude) {
          bounds.extend({ 
            lat: Number(pos.latitude), 
            lng: Number(pos.longitude) 
          });
        }
      });
      map.fitBounds(bounds);
    }
  };



  return (
    <APIProvider apiKey="AIzaSyBrofpZ8fdySj8Hn5rOc6jepjSZemsjHRo" libraries={['marker']}>
      <Map
        mapContainerStyle={mapContainerStyle}
        defaultCenter={initialCenter}
        defaultZoom={11}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
        mapId={'2d5d07f22b694022'}
        colorScheme={colorSchemeMap[mode]}
        onLoad={handleMapLoad}
      >
        <FleetMapContent 
          positions={positions} 
          loading={loading} 
        />
      </Map>
    </APIProvider>
  );
}